import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DeleteModal from "../../components/Common/DeleteModal";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import axios from "axios"; import { handleAuthError, toBase64Url } from "../../utils";
import withAuth from "../withAuth";
import CountrySelect from '../Custom/anibe/CountrySelect';
import TimezoneSelect from '../Custom/anibe/TimezoneSelect';
import InviteTeacherModal from "./InviteTeacherModal";
import { Col, Container, Row, Modal, ModalHeader, ModalBody, Form, Label, Input, FormFeedback } from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PaginationControls from "../../components/Common/PaginationControls";
import ActionsDropdown from "../../components/Common/ActionsDropdown";
import { useFormik } from "formik";
import * as Yup from 'yup';
import { baseUrl } from '../../Network';
import { useConfirm } from "../../contexts/ConfirmContext";

const WEEKDAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const ManageTeacher = () => {
  document.title = "Manage Teacher | RYD Admin";
  const { showConfirm } = useConfirm();

  const [loading, setLoading] = useState(true);
  const [teachers, setTeachers] = useState([]);
  const [teacherData, setTeacherData] = useState({});
  const [modal, setModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedTimezone, setSelectedTimezone] = useState('');
  const [searchQuery, setSearchQuery] = useState("");
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20,
    hasNextPage: false,
    hasPreviousPage: false
  });
  const [showAll, setShowAll] = useState(false);
  const [curriculumAccessLoadingId, setCurriculumAccessLoadingId] = useState(null);
  const [daysOff, setDaysOff] = useState(null);
  const [daysOffError, setDaysOffError] = useState(false);
  const [resourceAccessLoadingId, setResourceAccessLoadingId] = useState(null);

  useEffect(() => {
    fetchTeachers();
  }, [modal, currentPage, pageSize, searchQuery]);

  const fetchTeachers = async () => {
    try {
      setLoading(true);
      const limit = showAll ? 10000 : pageSize;
      const params = {
        page: currentPage,
        limit: limit,
        ...(searchQuery && { search: searchQuery })
      };
      const response = await axios.get(`${baseUrl}/admin/teacher/all`, { params });
      const responseData = response.data.data;
      const data = Array.isArray(responseData) ? responseData : (responseData?.data || []);
      const paginationData = responseData?.pagination || {
        currentPage: 1,
        totalPages: 1,
        totalItems: Array.isArray(data) ? data.length : 0,
        itemsPerPage: limit,
        hasNextPage: false,
        hasPreviousPage: false
      };
      setTeachers(Array.isArray(data) ? data : []);
      setPagination(paginationData);
      setLoading(false);
      fetchDaysOff();
    } catch (error) {
      handleAuthError(error)
      console.error("Error:", error);
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    if (size === 'all') {
      setShowAll(true);
      setPageSize(10000);
    } else {
      setShowAll(false);
      setPageSize(parseInt(size));
      setCurrentPage(1);
    }
  };

  const goToNextPage = () => {
    if (pagination.hasNextPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (pagination.hasPreviousPage) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const toggle = () => {
    setModal(!modal);
  };

  const handleCountryChange = (country) => {
    setSelectedCountry(country);
    setSelectedTimezone("");
  };

  const handleTimezoneChange = (timezone) => {
    setSelectedTimezone(timezone);
  };

  const handleTeacherClick = (teacher) => {
    if (teacher) {
      setTeacherData(teacher);
      setIsEdit(true);
      toggle();
    }
  };

  const onClickDelete = (teacher) => {
    if (teacher) {
      setTeacherData(teacher);
      setDeleteModal(true);
    } else {
      console.error("Teacher data is undefined.");
    }
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: teacherData.firstName || "",
      lastName: teacherData.lastName || "",
      email: teacherData.email || "",
      password: teacherData.password || "",
      gender: teacherData.gender || "",
      phone: teacherData.phone || "",
      qualification: teacherData.qualification || "",
      classLink: teacherData.classLink || "",
      docUrl: teacherData.docUrl || "",
      experience: teacherData.experience || "",

    },
    validationSchema: Yup.object({
      firstName: Yup.string().required("First Name is required"),
      lastName: Yup.string().required("Last Name is required"),
      email: Yup.string().email("Invalid email").required("Email is required"),
      // Password is only set when creating; edit never changes it (the API doesn't return it).
      password: isEdit ? Yup.string() : Yup.string().required("Password is required"),
      gender: Yup.string().required("Gender is required"),
      phone: Yup.string().required("Phone number is required"),
      qualification: Yup.string().required("Qualification is required"),
      docUrl: Yup.string().required("Document URL is required"),
      experience: Yup.string().required("Work Experience is required"),
    }),
    onSubmit: async (values) => {
      try {
        const newTeacher = {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          ...(isEdit ? {} : { password: values.password }),
          gender: values.gender,
          phone: values.phone,
          qualification: values.qualification,
          docUrl: values.docUrl,
          classLink: values.classLink,
          experience: values.experience,
        };


        let response;
        if (isEdit) {

          response = await axios.put(
            `${baseUrl}/admin/teacher/edit/${teacherData.id}`,
            newTeacher
          );
          toast.success("Teacher updated successfully");
        } else {

          response = await axios.post(
            `${baseUrl}/admin/teacher/create`,
            newTeacher
          );
          toast.success("Teacher created successfully");
        }

        const responseData = response.data;

        if (isEdit) {
          const updatedTeachers = teachers.map((teacher) =>
            teacher.id === teacherData.id ? { ...teacher, ...responseData } : teacher
          );
          setTeachers(updatedTeachers);
        } else {
          setTeachers([...teachers, responseData]);
        }

        toggle();
      } catch (error) {
        handleAuthError(error)
        console.error("Error:", error);
      }
    },
  });

  const handleDeleteTeacher = async () => {
    try {

      await axios.delete(`${baseUrl}/admin/teacher/${teacherData.id}`);
      const updatedTeachers = teachers.filter(
        (teacher) => teacher.id !== teacherData.id
      );
      setTeachers(updatedTeachers);
      setDeleteModal(false);
      toast.success("Teacher deleted successfully");
    } catch (error) {
      handleAuthError(error)
      console.error("Error:", error);
      toast.error("Failed to delete teacher");
    }
  };

  const inviteTeacher = async (email) => {
    try {
      const response = await axios.post(`${baseUrl}/admin/invite/teacher`, {
        email,
      });
      return response.data;
    } catch (error) {
      handleAuthError(error)
      throw error;
    }
  };

  const promptToggleTeacherCurriculumAccess = (teacher) => {
    const granting = !teacher.canCreateCurriculum;
    const fullName = `${teacher.firstName || ""} ${teacher.lastName || ""}`.trim() || "this teacher";
    showConfirm({
      title: granting ? "Grant curriculum access?" : "Revoke curriculum access?",
      message: granting
        ? `${fullName} will be able to upload curriculum to the AI Tutor workspace. Continue?`
        : `${fullName} will no longer be able to upload curriculum. Continue?`,
      variant: granting ? "primary" : "warning",
      confirmLabel: granting ? "Grant AI access" : "Revoke AI access",
    }).then((ok) => {
      if (ok) toggleTeacherCurriculumAccess(teacher);
    });
  };

  const toggleTeacherCurriculumAccess = async (teacher) => {
    const nextValue = !teacher.canCreateCurriculum;
    setCurriculumAccessLoadingId(teacher.id);
    try {
      await axios.put(`${baseUrl}/admin/teacher/curriculum-access/${teacher.id}`, {
        canCreateCurriculum: nextValue,
      });
      setTeachers((prev) =>
        prev.map((item) =>
          item.id === teacher.id ? { ...item, canCreateCurriculum: nextValue } : item
        )
      );
      toast.success(
        nextValue
          ? "Teacher can now manage curriculum"
          : "Teacher curriculum access removed"
      );
    } catch (error) {
      handleAuthError(error);
      toast.error("Failed to update curriculum access");
    } finally {
      setCurriculumAccessLoadingId(null);
    }
  };

  // Loaded separately so the teacher list does not wait for the Days Off report.
  const fetchDaysOff = async () => {
    setDaysOffError(false);
    try {
      const response = await axios.get(`${baseUrl}/admin/teacher/days-off`);
      if (!response.data?.status) throw new Error(response.data?.message || "Days Off unavailable");
      setDaysOff(response.data?.data && typeof response.data.data === "object" ? response.data.data : {});
    } catch (error) {
      handleAuthError(error);
      console.error("Error loading days off:", error);
      setDaysOffError(true);
    }
  };

  const promptToggleTeacherResourceAccess = (teacher) => {
    const granting = !teacher.canAccessResources;
    const fullName = `${teacher.firstName || ""} ${teacher.lastName || ""}`.trim() || "this teacher";
    showConfirm({
      title: granting ? "Grant resources access?" : "Revoke resources access?",
      message: granting
        ? `${fullName} will see and be able to open the Resources menu. Continue?`
        : `${fullName} will no longer see or be able to open Resources. Continue?`,
      variant: granting ? "primary" : "warning",
      confirmLabel: granting ? "Grant access" : "Revoke access",
    }).then((ok) => {
      if (ok) toggleTeacherResourceAccess(teacher);
    });
  };

  const toggleTeacherResourceAccess = async (teacher) => {
    const nextValue = !teacher.canAccessResources;
    setResourceAccessLoadingId(teacher.id);
    try {
      const response = await axios.put(`${baseUrl}/admin/teacher/resource-access/${teacher.id}`, {
        canAccessResources: nextValue,
      });
      if (!response.data?.status) throw new Error(response.data?.message || "Could not update resource access");
      setTeachers((prev) => prev.map((item) => item.id === teacher.id
        ? { ...item, canAccessResources: nextValue } : item));
      toast.success(nextValue ? "Resources access granted" : "Resources access revoked");
    } catch (error) {
      handleAuthError(error);
      toast.error("Could not update resource access");
    } finally {
      setResourceAccessLoadingId(null);
    }
  };

  // Deactivated teachers stay in this list (sorted last) but can't log in or be assigned classes.
  const promptToggleTeacherStatus = (teacher) => {
    const activating = teacher.status === false;
    const fullName = `${teacher.firstName || ""} ${teacher.lastName || ""}`.trim() || "this teacher";
    showConfirm({
      title: activating ? "Activate teacher?" : "Deactivate teacher?",
      message: activating
        ? `${fullName} will be able to log in and can be assigned classes again. Continue?`
        : `${fullName} will be signed out, won't be able to log in, and won't appear in any list for assigning classes or programs. Classes already assigned to them stay assigned until you reassign them. Continue?`,
      variant: activating ? "primary" : "warning",
      confirmLabel: activating ? "Activate" : "Deactivate",
    }).then((ok) => {
      if (ok) setTeacherStatus(teacher, activating);
    });
  };

  const setTeacherStatus = async (teacher, status) => {
    setCurriculumAccessLoadingId(teacher.id);
    try {
      const response = await axios.put(`${baseUrl}/admin/teacher/status/${teacher.id}`, { status });
      if (!response.data?.status) {
        toast.error(response.data?.message || "Failed to update teacher status");
        return;
      }
      const activeProgramCount = response.data?.data?.activeProgramCount || 0;
      if (status) {
        toast.success("Teacher activated");
      } else if (activeProgramCount > 0) {
        toast.warning(`Teacher deactivated. ${activeProgramCount} active program(s) are still assigned to them; reassign them from Manage Program.`);
      } else {
        toast.success("Teacher deactivated");
      }
      fetchTeachers();
    } catch (error) {
      handleAuthError(error);
      toast.error(error.response?.data?.message || "Failed to update teacher status");
    } finally {
      setCurriculumAccessLoadingId(null);
    }
  };

  // Active teachers first, deactivated last (the API already orders this way; keep it stable per page).
  const sortedTeachers = Array.isArray(teachers)
    ? [...teachers].sort((a, b) => Number(b.status !== false) - Number(a.status !== false))
    : [];

  const removeInviteTeacher = async (id) => {
    try {

      const response = await axios.delete(
        `${baseUrl}/admin/remove/invite/teacher/${id}`
      );
      return response.data;
    } catch (error) {
      handleAuthError(error)
      throw error;
    }
  };

  const toggleInviteModal = () => {
    setInviteModalOpen(!inviteModalOpen);
  };

  const shortenUrl = (url, maxLength) => {
    if (url.length <= maxLength) {
      return url;
    } else {
      return url.substring(0, maxLength) + "...";
    }
  };

   const token = localStorage.getItem('token');

  function openTacherogin(teacherId) {
  
    const payload = {
      adminToken: token,
      teacherId,
      timestamp: Date.now(),
    };

    const code = toBase64Url(payload);
    // const url = "http://localhost:3001"
    const url = "https://teacher.rydlearning.com"
    const partnerUrl = `${url}/auth/redirect?code=${encodeURIComponent(code)}`;
    window.open(partnerUrl, "_blank", "noopener,noreferrer");
  }

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteTeacher}
        onCloseClick={() => setDeleteModal(false)}
      />
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Dashboard" breadcrumbItem="Manage Teacher" />
          <Row>
            <Col lg="12">
              <Row className="align-items-center">
                <Col md={6}>
                  <div className="mb-3">
                    <h5 className="card-title">
                      Teacher List{" "}
                      <span className="text-muted fw-normal ms-2">
                        ({teachers.length})
                      </span>
                    </h5>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="d-flex flex-wrap align-items-center justify-content-end gap-2 mb-3 p-3 bg-light rounded filter-bar">
                    <div>
                      <Input
                        type="text"
                        placeholder="Search"
                        value={searchQuery}
                        onChange={handleSearch}
                      />
                    </div>
                    <div>
                      {/*  <Link
                        to="#"
                        className="btn btn-light"
                        onClick={() => {
                          setTeacherData({});
                          setIsEdit(false);
                          toggle();
                        }}
                      >
                        <i className="bx bx-plus me-1"></i> Add New Teacher
                      </Link>

                      */}


                      <Link
                        to="#"
                        className="ms-2 btn btn-primary"
                        onClick={() => {
                          setTeacherData({});
                          setIsEdit(false);
                          toggleInviteModal(); // Open invite teacher modal
                        }}
                      >
                        <i className="mdi mdi-email-variant me-1"></i> Invite
                        Teacher
                      </Link>

                      <Link
                        to="/manage-auto-assignment"
                        className="ms-2 btn btn-secondary"
                      >
                        Auto Assignment
                      </Link>
                    </div>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col xl="12">
                  {loading ? (
                    <div className="text-center mt-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : teachers.length === 0 ? (
                    <div className="text-center mt-5">
                      <h3>No data available</h3>
                    </div>
                  ) : (
                    <div className="table-responsive">
                    <table className="table align-middle">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Firstname</th>
                          <th>Lastname</th>
                          <th>Email</th>
                          <th>Gender</th>
                          <th>Phone</th>
                          <th>Country</th>
                          <th>Partners</th>
                          <th>Timezone</th>
                          <th title="Recurring off weekdays per not-started or ongoing cohort, in the teacher timetable's Africa/Lagos timezone">Days Off (WAT)</th>
                          <th>Cert.</th>
                          <th>Docs</th>
                          <th>Experience</th>
                          <th>Class Link</th>
                          <th>Resources Access</th>
                          <th>Curriculum Access</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedTeachers.map((teacher, index) => {
                            const globalIndex = showAll
                              ? index
                              : (pagination.currentPage - 1) * pagination.itemsPerPage + index;
                            const isInactive = teacher.status === false;
                            return (
                              <tr key={teacher.id || index} className={isInactive ? "text-muted" : undefined} style={isInactive ? { opacity: 0.65 } : undefined}>
                                <td>{globalIndex + 1}</td>
                              <td>{teacher.firstName}</td>
                              <td>{teacher.lastName}</td>
                              <td>{teacher.email}</td>
                              <td>{teacher.gender}</td>
                              <td>{teacher.phone}</td>
                              <td>{teacher.country}</td>
                              <td>{Array.isArray(teacher.partnerNames) && teacher.partnerNames.length ? teacher.partnerNames.join(", ") : "—"}</td>
                              <td>{teacher.timezone}</td>
                              <td>
                                {(() => {
                                  if (daysOffError) return <span className="text-muted">Unavailable</span>;
                                  if (daysOff === null) return <span className="text-muted">Loading...</span>;
                                  const info = daysOff[teacher.id];
                                  if (!info || !info.byCohort?.length) return <span className="text-muted">No cohort classes</span>;
                                  return (
                                    <div className="d-flex flex-column gap-1">
                                      {info.byCohort.map((cohort) => (
                                        <div key={cohort.cohortId}>
                                          <span className="fw-semibold">{cohort.title || `Cohort ${cohort.cohortId}`}:</span>{" "}
                                          {cohort.scheduleIncomplete
                                            ? <span className="text-warning">Schedule incomplete</span>
                                            : cohort.offWeekdays?.length
                                            ? cohort.offWeekdays.map((day) => WEEKDAY_NAMES[day]).join(", ")
                                            : <span className="text-muted">No weekly days off</span>}
                                        </div>
                                      ))}
                                    </div>
                                  );
                                })()}
                              </td>
                              <td>{teacher.qualification}</td>
                              <td>
                                <a target={'_blank'} rel="noopener noreferrer" href={teacher.docUrl}>Docs</a>
                              </td>
                              <td>{teacher.experience}</td>
                              <td>
                                <a target={'_blank'} rel="noopener noreferrer" href={teacher.classLink}><i className="mdi mdi-link font-size-18"></i></a>
                              </td>
                              <td>
                                <span className={`badge ${teacher.canAccessResources ? "bg-success" : "bg-danger"}`}>
                                  {teacher.canAccessResources ? "Yes" : "No"}
                                </span>
                              </td>
                              <td>
                                <span className={`badge ${teacher.canCreateCurriculum ? "bg-success" : "bg-danger"}`}>
                                  {teacher.canCreateCurriculum ? "Yes" : "No"}
                                </span>
                              </td>
                              <td>
                                <span className={`badge ${isInactive ? "bg-secondary" : "bg-success"}`}>
                                  {isInactive ? "Inactive" : "Active"}
                                </span>
                              </td>
                              <td>
                                <ActionsDropdown
                                  toggleTitle="Teacher actions"
                                  loading={curriculumAccessLoadingId === teacher.id || resourceAccessLoadingId === teacher.id}
                                  items={[
                                    { label: "Edit", icon: "mdi-pencil", onClick: () => handleTeacherClick(teacher), variant: "success" },
                                    {
                                      label: teacher.canAccessResources ? "Revoke Resources Access" : "Grant Resources Access",
                                      icon: teacher.canAccessResources ? "mdi-lock-off" : "mdi-lock-open-check",
                                      onClick: () => promptToggleTeacherResourceAccess(teacher),
                                      variant: teacher.canAccessResources ? "warning" : "primary",
                                    },
                                    {
                                      label: teacher.canCreateCurriculum ? "Revoke AI Access" : "Grant AI Access",
                                      icon: teacher.canCreateCurriculum ? "mdi-lock-off" : "mdi-lock-open-check",
                                      onClick: () => promptToggleTeacherCurriculumAccess(teacher),
                                      variant: teacher.canCreateCurriculum ? "warning" : "primary",
                                    },
                                    {
                                      label: isInactive ? "Activate" : "Deactivate",
                                      icon: isInactive ? "mdi-account-check" : "mdi-account-off",
                                      onClick: () => promptToggleTeacherStatus(teacher),
                                      variant: isInactive ? "success" : "warning",
                                    },
                                    { label: "Delete", icon: "mdi-delete", onClick: () => onClickDelete(teacher), variant: "danger" },
                                    { label: "Explore account", icon: "mdi-open-in-new", onClick: () => showConfirm({ title: "Explore account", message: "Do you really want to explore " + (teacher?.firstName || "") + " " + (teacher?.lastName || "") + "'s account?", variant: "primary" }).then((ok) => { if (ok) openTacherogin(teacher.id); }), variant: "success" },
                                  ]}
                                />
                              </td>
                            </tr>
                            );
                          })}
                      </tbody>
                    </table>
                    </div>
                  )}
                  {Array.isArray(teachers) && teachers.length > 0 && (
                    <PaginationControls
                      currentPage={pagination.currentPage}
                      totalPages={pagination.totalPages}
                      totalItems={pagination.totalItems}
                      pageSize={pagination.itemsPerPage}
                      showAll={showAll}
                      handlePageChange={handlePageChange}
                      handlePageSizeChange={handlePageSizeChange}
                      goToNextPage={goToNextPage}
                      goToPreviousPage={goToPreviousPage}
                      canPreviousPage={pagination.hasPreviousPage}
                      canNextPage={pagination.hasNextPage}
                      paginatedDataLength={teachers.length}
                    />
                  )}

                  <Modal isOpen={modal} toggle={toggle}>
                    <ModalHeader toggle={toggle}>
                      {isEdit ? "Edit Teacher" : "Add New Teacher"}
                    </ModalHeader>
                    <ModalBody>
                      <Form onSubmit={validation.handleSubmit}>
                        <Row>
                          <Col xs={12}>
                            <Row>
                              <Col xs={12} md={6}>
                                <div className="mb-3">
                                  <Label className="form-label">Firstname</Label>
                                  <Input
                                    name="firstName"
                                    type="text"
                                    placeholder="First Name"
                                    onChange={validation.handleChange}
                                    value={validation.values.firstName}
                                    invalid={validation.errors.firstName}
                                  />
                                  {validation.errors.firstName && (
                                    <FormFeedback>{validation.errors.firstName}</FormFeedback>
                                  )}
                                </div>
                              </Col>
                              <Col xs={12} md={6}>
                                <div className="mb-3">
                                  <Label className="form-label">Lastname</Label>
                                  <Input
                                    name="lastName"
                                    type="text"
                                    placeholder="Last Name"
                                    onChange={validation.handleChange}
                                    value={validation.values.lastName}
                                    invalid={validation.errors.lastName}
                                  />
                                  {validation.errors.lastName && (
                                    <FormFeedback>{validation.errors.lastName}</FormFeedback>
                                  )}
                                </div>
                              </Col>
                            </Row>
                            <div className="mb-3">
                              <Label className="form-label">Email</Label>
                              <Input
                                name="email"
                                type="email"
                                placeholder="Email"
                                onChange={validation.handleChange}
                                value={validation.values.email}
                                invalid={validation.errors.email}
                              />
                              {validation.errors.email && (
                                <FormFeedback>{validation.errors.email}</FormFeedback>
                              )}
                            </div>
                            {!isEdit && (
                              <>
                                <div className="mb-3">
                                  <Label className="form-label">Password</Label>
                                  <Input
                                    name="password"
                                    type="password"
                                    placeholder="Password"
                                    onChange={validation.handleChange}
                                    value={validation.values.password}
                                    invalid={validation.errors.password}
                                  />
                                  {validation.errors.password && (
                                    <FormFeedback>{validation.errors.password}</FormFeedback>
                                  )}

                                </div>


                              </>

                            )}
                            <Row>
                              <Col xs={12} md={6}>
                                <div className="mb-3">
                                  <Label className="form-label">Gender</Label>
                                  <Input
                                    type="select"
                                    name="gender"
                                    onChange={validation.handleChange}
                                    value={validation.values.gender}
                                    invalid={validation.errors.gender}
                                  >
                                    <option value="">Select Gender</option>
                                    <option value="Male">Male</option>
                                    <option value="Female">Female</option>
                                  </Input>
                                  {validation.errors.gender && (
                                    <FormFeedback>{validation.errors.gender}</FormFeedback>
                                  )}
                                </div>
                              </Col>
                              <Col xs={12} md={6}>
                                <div className="mb-3">
                                  <Label className="form-label">Phone</Label>
                                  <Input
                                    name="phone"
                                    type="text"
                                    placeholder="Phone Number"
                                    onChange={validation.handleChange}
                                    value={validation.values.phone}
                                    invalid={validation.errors.phone}
                                  />
                                  {validation.errors.phone && (
                                    <FormFeedback>{validation.errors.phone}</FormFeedback>
                                  )}
                                </div>
                              </Col>
                            </Row>



                            <Row>

                              {!isEdit && (
                                <>

                                  <div className="mb-3">
                                    <Label className="form-label">Qualification</Label>
                                    <Input
                                      name="qualification"
                                      type="text"
                                      placeholder="Qualification"
                                      onChange={validation.handleChange}
                                      value={validation.values.qualification}
                                      invalid={validation.errors.qualification}
                                    />
                                    {validation.errors.qualification && (
                                      <FormFeedback>{validation.errors.qualification}</FormFeedback>
                                    )}
                                  </div>

                                </>




                              )}
                              {isEdit && (
                                <>

                                  <div className="mb-3">
                                    <Label className="form-label">Class Link</Label>
                                    <Input
                                      name="classLink"
                                      type="text"
                                      placeholder="Class Link"
                                      onChange={validation.handleChange}
                                      value={validation.values.classLink}
                                      invalid={validation.errors.classLink}
                                    />
                                    {validation.errors.classLink && (
                                      <FormFeedback>{validation.errors.classLink}</FormFeedback>
                                    )}
                                  </div>

                                </>

                              )}
                            </Row>
                            <Row>
                              <Col md={6}>
                                <div className="mb-3">
                                  <Label className="form-label">CV Document URL</Label>
                                  <Input
                                    name="docUrl"
                                    type="text"
                                    placeholder="Document URL"
                                    onChange={validation.handleChange}
                                    value={validation.values.docUrl}
                                    invalid={validation.errors.docUrl}
                                  />
                                  {validation.errors.docUrl && (
                                    <FormFeedback>{validation.errors.docUrl}</FormFeedback>
                                  )}
                                </div>
                              </Col>
                              <Col md={6}>
                                <div className="mb-3">
                                  <Label className="form-label">Work Experience</Label>
                                  <Input
                                    name="experience"
                                    type="text"
                                    placeholder="Work Experience (e.g 2 years)"
                                    onChange={validation.handleChange}
                                    value={validation.values.experience}
                                    invalid={validation.errors.experience}
                                  />
                                  {validation.errors.experience && (
                                    <FormFeedback>{validation.errors.experience}</FormFeedback>
                                  )}
                                </div>
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                        <Row>
                          <Col>
                            <div className="text-end">
                              {!isEdit ? (
                                <button
                                  type="submit"
                                  className="btn btn-primary save-teacher"
                                >
                                  Create Teacher
                                </button>
                              ) : (
                                <button
                                  type="submit"
                                  className="btn btn-primary save-teacher"
                                >
                                  Update Teacher
                                </button>
                              )}
                            </div>
                          </Col>
                        </Row>
                      </Form>
                    </ModalBody>
                  </Modal>

                  <InviteTeacherModal
                    isOpen={inviteModalOpen}
                    toggle={toggleInviteModal}
                    inviteTeacher={inviteTeacher}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </div>
      <ToastContainer />
    </React.Fragment>
  );
};

export default withAuth(ManageTeacher);
